import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, ButtonBase, Collapse, Divider, Link, Skeleton, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TONE_META } from '../Dashboard/AiSummaryCard';

/*
  InsightsPanel: the collapsible "AI Insights" footer of an entity card.
  Collapsed by default; the first expand calls `fetchAnalysis`, after which
  the backend cache makes reopening free. Expanded/collapsed state is
  remembered per entity so a card the user cares about stays open.

  When the backend answers 503 (no ANTHROPIC_API_KEY) the row removes itself
  — matching AiSummaryCard — and a module-wide flag stops every other card
  from repeating the doomed request this session.

  Props:
   - storageKey: string — unique per entity, e.g. 'category:groceries'
   - fetchAnalysis: (force) => Promise<{ analysis, generatedAt, cached }>
     where analysis is { headline, insights: [{ tone, text }], ... }
   - disabled: hide the panel entirely (e.g. transfer categories)
*/

let aiUnavailable = false; // one 503 hides the row on every card

// Analyses cached under older prompt versions can carry more observations
// than the current headline-plus-two layout allows.
const MAX_INSIGHTS = 2;

const openKey = (storageKey) => `aiInsightsOpen:${storageKey}`;

const InsightsPanel = ({ storageKey, fetchAnalysis, disabled = false }) => {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(openKey(storageKey)) === '1';
    } catch {
      return false;
    }
  });
  const [analysis, setAnalysis] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(aiUnavailable);
  // Generations left in the user's global daily AI allowance (shared across
  // the whole app); null until the backend has said. Drives the Refresh link.
  const [remaining, setRemaining] = useState(null);
  // Ignore responses from superseded requests (rapid toggle/refresh).
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (force) => {
      if (aiUnavailable) {
        setUnavailable(true);
        return;
      }
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const res = await fetchAnalysis(force);
        if (requestId !== requestIdRef.current) return;
        setAnalysis(res.analysis);
        setGeneratedAt(res.generatedAt);
        if (typeof res.aiCredits?.remaining === 'number') setRemaining(res.aiCredits.remaining);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        if (err.response?.status === 503) {
          aiUnavailable = true;
          setUnavailable(true);
        } else {
          if (typeof err.response?.data?.aiCredits?.remaining === 'number') {
            setRemaining(err.response.data.aiCredits.remaining);
          }
          setError(err.response?.data?.error || 'Could not generate insights right now.');
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [fetchAnalysis]
  );

  // A panel restored open from a previous visit fetches straight away.
  useEffect(() => {
    if (open && !disabled && !analysis && !loading && !error) load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(openKey(storageKey), next ? '1' : '0');
    } catch {
      // localStorage unavailable — state just won't persist
    }
    if (next && !analysis && !loading) load(false);
  };

  if (unavailable) return null;

  if (disabled) {
    return (
      <Box sx={{ mt: 'auto', pt: 1 }}>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            py: 1,
            px: 0.25,
            color: 'text.disabled',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: '0.95rem' }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            AI Insights
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 'auto', pt: 1 }}>
      <Divider />
      <ButtonBase
        onClick={toggle}
        aria-expanded={open}
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          gap: 0.75,
          py: 1,
          px: 0.25,
          borderRadius: 1,
          color: 'secondary.main',
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: '0.95rem' }} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          AI Insights
        </Typography>
        <ExpandMoreIcon
          sx={{
            ml: 'auto',
            fontSize: '1.1rem',
            color: 'text.disabled',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms ease',
          }}
        />
      </ButtonBase>
      <Collapse in={open} unmountOnExit={false}>
        <Box sx={{ pb: 1 }}>
          {loading && !analysis ? (
            <Box>
              <Skeleton width="92%" height={16} />
              <Skeleton width="78%" height={16} />
              <Skeleton width="85%" height={16} />
            </Box>
          ) : error && !analysis ? (
            <Typography variant="caption" color="text.secondary">
              {error}{' '}
              <Link component="button" type="button" variant="caption" onClick={() => load(false)}>
                Try again
              </Link>
            </Typography>
          ) : analysis ? (
            <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 150ms ease' }}>
              {analysis.headline && (
                <Typography variant="body3" sx={{ display: 'block', fontWeight: 600, mb: 0.75, fontSize: '0.8125rem' }}>
                  {analysis.headline}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {(analysis.insights || []).slice(0, MAX_INSIGHTS).map((insight, index) => {
                  const { Icon, color } = TONE_META[insight.tone] || TONE_META.info;
                  return (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Icon sx={{ fontSize: '0.9rem', color, mt: '2px', flexShrink: 0 }} />
                      <Typography variant="body3" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                        {insight.text}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                {generatedAt ? `Updated ${new Date(generatedAt).toLocaleDateString()}` : ''}
                {' · '}
                {remaining === 0 ? (
                  'Daily refresh limit reached'
                ) : (
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => load(true)}
                    sx={{ color: 'secondary.main', fontWeight: 600 }}
                  >
                    Refresh
                  </Link>
                )}
              </Typography>
              {error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {error}
                </Typography>
              )}
            </Box>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
};

export default InsightsPanel;
